import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import exportTranslates from './translates/export'
import importTranslates from './translates/import'

const prepareRecipeToSave = item => {
  return {
    ...item,
    name: item.name.trim(),
    image: item.image ? item.image : undefined,
  }
}

const saveVariants = async ({ recipeId, variantsModel, addVariant, deleteVariant, updateVariant }) => {
  if (variantsModel.isDirty) {
    const { initialState, values: variants } = variantsModel
    const toUpdate = variants.filter(variant => {
      if (!variant.id) {
        return false
      }
      const initial = initialState.values.find(initialVariant => initialVariant.id === variant.id) || {}
      return JSON.stringify(initial) !== JSON.stringify(variant)
    })
    const toAdd = variants.filter(variant => !variant.id)
    const variantsLeft = variants.map(v => v.id).filter(Boolean)
    const toDelete = initialState.values.filter(
      initialVariant => initialVariant.id && !variantsLeft.includes(initialVariant.id),
    )

    if (toUpdate && toUpdate.length) {
      await Promise.all(
        toUpdate.map(variant => updateVariant(recipeId, variant.id, variant)),
      )
    }

    if (toAdd && toAdd.length) {
      await Promise.all(
        toAdd.map(variant => addVariant(recipeId, variant).then(newVariant => {
          variant.id = newVariant.id
        })),
      )
    }

    if(toDelete && toDelete.length) {
      await Promise.all(
        toDelete.map(variant => deleteVariant(recipeId, variant.id)),
      )
    }
  }
}


export default ({ addRecipe, updateRecipe, deleteRecipe, history, resetDirty, addVariant, deleteVariant, updateVariant }) => {

  const [saving, setSaving] = useState(false)

  const onSave = useCallback(
    async (recipeModel, variantsModel) => {
      try {
        setSaving(true)

        const recipeId = recipeModel.values.id

        if (recipeModel.isDirty) {
          const recipe = recipeModel.values
          const result = prepareRecipeToSave(recipe)
          delete result.id
          await updateRecipe(recipeId, result)
        }

        await saveVariants({ recipeId, variantsModel, addVariant, deleteVariant, updateVariant })

        toast.success(`Recipe '${recipeModel.values.name}' updated`, {
          position: toast.POSITION.TOP_RIGHT,
        })
        setSaving(false)
        resetDirty()

      } catch(e) {
        setSaving(false)
      }
    },
    [addVariant, deleteVariant, resetDirty, updateRecipe, updateVariant],
  )

  const onAdd = useCallback(
    async (recipeModel, variantsModel) => {
      try {
        setSaving(true)

        const recipe = recipeModel.values
        const result = prepareRecipeToSave(recipe)

        const newRecipe = await addRecipe(result)

        let variantsSaved = false
        try {
          await saveVariants({ recipeId: newRecipe.id, variantsModel, addVariant, deleteVariant, updateVariant })
          variantsSaved = true
        } catch (updateErr) {
          await deleteRecipe(newRecipe.id)
        }
        if (variantsSaved) {
          resetDirty()
          setSaving(false)

          toast.success(`Recipe '${result.name}' added`, {
            position: toast.POSITION.TOP_RIGHT,
          })

          history.replace(`/recipes/${newRecipe.id}`)
        } else {
          setSaving(false)
        }
      } catch(e) {
        setSaving(false)
      }
    },
    [addRecipe, addVariant, deleteRecipe, deleteVariant, history, resetDirty, updateVariant],
  )

  const onExport = useCallback((recipeModel, variationsModel, locales) => exportTranslates(recipeModel, variationsModel, locales), [])
  const onImport = useCallback((file, recipeModel, variationsModel, formEl) => importTranslates(file, recipeModel, variationsModel, formEl), [])

  return {
    onAdd,
    onSave,
    saving,
    onExport,
    onImport,
  }
}
